import config from '@/utils/ApiUtils'
import { fetchWithToken } from '@/utils/Keycloak'

// Écriture JSON vers le microservice : renvoie le corps, ou lève l'erreur relayée par Strapp
const sendJson = (path, method, body) =>
  fetchWithToken(`${config().strappBackend}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body && { body: JSON.stringify(body) })
  })
    .then(response => {
      if (!response.ok) {
        return response.json().catch(() => null).then(error => {
          throw new Error((error && (error.error || error.message)) || `Erreur API ${response.status}`)
        })
      }
      return response.json()
    })

/**
 * Service pour interagir avec l'API Strapp (fournisseur devices/SIM)
 * Base : Knox -> Strapp (Strapp vient toujours après Knox)
 * Pagination par curseur (limit + cursor)
 */
export default {
  /**
   * Récupère toutes les données Strapp en un appel
   * Utilise le cache Redis backend (TTL 5min)
   * @param {boolean} force - Forcer refresh cache (bypass Redis)
   * @param {string} idStrapp - ID Strapp pour filtrer les données
   * @returns {Promise<Object>} { sims, subscriptions, models, sites, billingEntities, accessories, orders, stats, cached, generatedAt }
   */
  getDatas(force = false, idStrapp = null) {
    const params = []
    if (force) params.push('force=true')
    if (idStrapp) params.push(`idStrapp=${idStrapp}`)
    
    const queryString = params.length > 0 ? `?${params.join('&')}` : ''
    const url = `${config().strappBackend}datas${queryString}`
    console.log('[StrappService] Calling GET', url)
    
    return fetchWithToken(url, { method: 'GET' })
      .then(response => {
        console.log('[StrappService] Response received from /datas, status:', response.status)
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] API Error:', error)
            throw new Error(error.message || 'API Error')
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error('[StrappService] Error fetching datas:', error)
        throw error
      })
  },

  /**
   * Liste tous les terminaux (devices)
   * @param {Object} filters - Filtres optionnels
   * @param {string} filters.id - ID exact
   * @param {string} filters.imei - IMEI exact
   * @param {string} filters.serial - Serial exact
   * @param {Array<string>} filters.statuses - Liste de statuts
   * @param {string} filters.user - ID utilisateur
   * @param {string} filters.model - ID modèle
   * @param {string} filters.site - ID site
   * @param {string} filters.idStrapp - ID Strapp
   * @param {string|Array<string>} filters.populate - Relations à peupler (chaque valeur envoyée séparément: populate=model&populate=user). Valeurs: model, user, owner, company, billingEntity, supplier, site, stockSite
   * @param {number} filters.limit - Limite résultats (1-100, défaut 100)
   * @param {number} filters.cursor - Curseur pagination
   * @returns {Promise<Object>} { data, cursor, remaining }
   */
  getDevices(filters = {}) {
    const params = new URLSearchParams()
    
    // Paramètres de base sans populate par défaut
    if (filters.id) params.append('id', filters.id)
    if (filters.imei) params.append('imei', filters.imei)
    if (filters.serial) params.append('serial', filters.serial)
    if (filters.statuses && filters.statuses.length > 0) {
      params.append('statuses', filters.statuses.join(','))
    }
    if (filters.user) params.append('user', filters.user)
    if (filters.model) params.append('model', filters.model)
    if (filters.site) params.append('site', filters.site)
    if (filters.idStrapp) params.append('idStrapp', filters.idStrapp)
    
    // Populate : string unique avec valeurs séparées par virgules
    if (filters.populate) {
      params.append('populate', filters.populate)
    }
    
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)
    if (filters.force) params.append('force', 'true')

    // URLSearchParams encode les espaces avec '+' mais l'API Strapp attend '%20'
    const queryString = params.toString().replace(/\+/g, '%20')
    const url = `${config().strappBackend}devices${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { 
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] API Error:', error)
            throw new Error(error.error || error.message || 'API Error')
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error('[StrappService] Error fetching devices:', error)
        throw error
      })
  },

  /**
   * Récupère TOUS les devices Strapp en gérant la pagination automatiquement
   * @param {Object} filters - Filtres (sans limit/cursor, gérés en interne)
   * @returns {Promise<Array>} Tableau complet de tous les devices correspondant aux filtres
   */
  getAllDevices(filters = {}) {
    const fetchPage = (cursor, accumulated) => {
      return this.getDevices({ ...filters, limit: 100, cursor })
        .then(response => {
          const devices = response?.data || response?.results || []
          const allSoFar = accumulated.concat(devices)
          const remaining = response?.remaining ?? 0
          if (remaining > 0 && devices.length > 0) {
            const nextCursor = response?.cursor != null ? response.cursor : cursor + 100
            return fetchPage(nextCursor, allSoFar)
          }
          return allSoFar
        })
    }
    return fetchPage(0, [])
  },

  /**
   * Liste tous les utilisateurs (collaborateurs) rattachés à l'entreprise
   * @param {Object} filters - Filtres optionnels
   * @param {string} filters.id - Filtrer par ID exact
   * @param {string} filters.email - Recherche partielle sur l'email (insensible à la casse)
   * @param {string} filters.firstName - Recherche partielle sur le prénom (insensible à la casse)
   * @param {string} filters.lastName - Recherche partielle sur le nom (insensible à la casse)
   * @param {string} filters.site - Filtrer par ID du site géographique
   * @param {string} filters.billingEntity - Filtrer par ID de l'entité de facturation
   * @param {string|Array<string>} filters.populate - Relations à peupler. Valeurs: site, billingEntity
   * @param {number} filters.limit - Limite résultats (1-100, défaut 100, max 100)
   * @param {number} filters.cursor - Index de départ pour la pagination
   * @returns {Promise<Object>} { results, cursor, remaining }
   */
  getUsers(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.email) params.append('email', filters.email)
    if (filters.firstName) params.append('firstName', filters.firstName)
    if (filters.lastName) params.append('lastName', filters.lastName)
    if (filters.site) params.append('site', filters.site)
    if (filters.billingEntity) params.append('billingEntity', filters.billingEntity)
    if (filters.populate) {
      const populateValues = Array.isArray(filters.populate) ? filters.populate : [filters.populate]
      if (populateValues.length > 0) params.append('populate', populateValues.join(','))
    }
    // Limite stricte : max 100 selon l'API
    const safeLimit = filters.limit ? Math.min(filters.limit, 100) : 100
    params.append('limit', safeLimit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().strappBackend}users${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { 
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] API Error:', error)
            throw new Error(error.message || 'API Error')
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error('[StrappService] Error fetching users:', error)
        throw error
      })
  },

  /**
   * Récupère TOUS les utilisateurs Strapp en gérant la pagination automatiquement
   * @param {Object} filters - Filtres (sans limit/cursor, gérés en interne)
   * @returns {Promise<Array>} Tableau complet de tous les utilisateurs
   */
  getAllUsers(filters = {}) {
    const fetchPage = (cursor, accumulated) => {
      return this.getUsers({ ...filters, limit: 100, cursor })
        .then(response => {
          const users = response?.results || response?.data || []
          const allSoFar = accumulated.concat(users)
          const remaining = response?.remaining ?? 0
          if (remaining > 0 && users.length > 0) {
            const nextCursor = response?.cursor != null ? response.cursor : cursor + 100
            return fetchPage(nextCursor, allSoFar)
          }
          return allSoFar
        })
    }
    return fetchPage(0, [])
  },

  /**
   * Récupère un utilisateur Strapp par son ID
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} Détails de l'utilisateur
   */
  getUserById(userId, force = false) {
    const url = `${config().strappBackend}users/${userId}${force ? '?force=true' : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] API Error:', error)
            throw new Error(error.message || 'API Error')
          })
        }
        return response.json()
      })
      .then(data => {
        // L'API retourne {results: [...], cursor, remaining, count}
        // On extrait le premier utilisateur des résultats
        return data.results?.[0] || null
      })
      .catch(error => {
        console.error('[StrappService] Error fetching user by id:', error)
        throw error
      })
  },

  /**
   * Met à jour un utilisateur Strapp
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} data - Données à mettre à jour (l'id doit être inclus dans data)
   * @returns {Promise<Object>} Utilisateur mis à jour
   */
  updateUser(userId, data) {
    const url = `${config().strappBackend}users`

    // S'assurer que l'ID est dans le body
    const payload = {
      ...data,
      id: userId
    }

    return fetchWithToken(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] API Error:', error)
            throw new Error(error.message || 'API Error')
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error('[StrappService] Error updating user:', error)
        throw error
      })
  },

  /**
   * Archive (supprime) un utilisateur Strapp
   * @param {string} userId - ID de l'utilisateur
   * @param {Object} userData - Données de l'utilisateur (email, firstName, lastName requis par l'API)
   * @returns {Promise<Object>}
   */
  archiveUser(userId, userData = {}) {
    return this.updateUser(userId, {
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      archived: true
    })
  },

  /**
   * Récupère les device par utilisateur Strapp
   * @param {string} userId - ID de l'utilisateur Strapp
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<Object>} { results, cursor, remaining }
   */
  getUserDevices(userId, filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.populate) params.append('populate', filters.populate)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().strappBackend}users/${userId}/devices${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, { 
      method: 'GET'
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] API Error:', error)
            throw new Error(error.message || 'API Error')
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error(`[StrappService] Error fetching devices for user ${userId}:`, error)
        throw error
      })
  },

  /**
   * Liste les modèles de terminaux
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Liste modèles
   */
  getModels(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.name) params.append('name', filters.name)
    if (filters.brand) params.append('brand', filters.brand)
    if (filters.os) params.append('os', filters.os)
    if (filters.type) params.append('type', filters.type)
    if (filters.esimCompatible !== undefined) params.append('esimCompatible', filters.esimCompatible)
    if (filters.reconditionned !== undefined) params.append('reconditionned', filters.reconditionned)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)

    const queryString = params.toString()
    const url = `${config().strappBackend}models${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching models:', error)
        throw error
      })
  },

  /**
   * Liste les entités de facturation
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Entités de facturation
   */
  getBillingEntities(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.name) params.append('name', filters.name)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)

    const queryString = params.toString()
    const url = `${config().strappBackend}billing-entities${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching billing entities:', error)
        throw error
      })
  },

  /**
   * Liste les sites géographiques
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Sites
   */
  getSites(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.name) params.append('name', filters.name)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)

    const queryString = params.toString()
    const url = `${config().strappBackend}sites${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching sites:', error)
        throw error
      })
  },

  /**
   * Crée ou met à jour un utilisateur (upsert)
   * @param {Object} userData - Données utilisateur
   * @returns {Promise<Object>} Utilisateur créé/MAJ
   */
  upsertUser(userData) {
    return fetchWithToken(`${config().strappBackend}users`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => {
            console.error('[StrappService] upsertUser API Error:', error)
            throw new Error(error.message || `Erreur API ${response.status}`)
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error('[StrappService] Error upserting user:', error)
        throw error
      })
  },

  /**
   * Liste les abonnements SIM disponibles
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Abonnements
   */
  getSubscriptions(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.name) params.append('name', filters.name)
    if (filters.operator) params.append('operator', filters.operator)
    if (filters.dataOnly !== undefined) params.append('dataOnly', filters.dataOnly)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)

    const queryString = params.toString()
    const url = `${config().strappBackend}subscriptions${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching subscriptions:', error)
        throw error
      })
  },

  /**
   * Liste les accessoires disponibles
   * @returns {Promise<Object>} Accessoires
   */
  getAccessories() {
    return fetchWithToken(`${config().strappBackend}accessories`, {
      method: 'GET'
    })
      .then(response => response.json())
      .then(data => {
        const results = Array.isArray(data)
          ? data
          : (data?.results || data?.data || [])

        const cursor = Array.isArray(data)
          ? 0
          : data?.cursor

        const remaining = Array.isArray(data)
          ? 0
          : (data?.remaining || 0)

        const total = Array.isArray(data)
          ? data.length
          : (data?.total || results.length)

        // Supporte les deux formats backend (tableau direct ou réponse paginée)
        return {
          results,
          cursor,
          remaining,
          total
        }
      })
      .catch(error => {
        console.error('[StrappService] Error fetching accessories:', error)
        throw error
      })
  },

  /**
   * Liste les lignes SIM (exclut résiliées par défaut)
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Lignes SIM
   */
  getSIMs(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.iccid) params.append('iccid', filters.iccid)
    if (filters.phoneNumber) params.append('phoneNumber', filters.phoneNumber)
    if (filters.status) params.append('status', filters.status)
    if (filters.state) params.append('state', filters.state)
    if (filters.operator) params.append('operator', filters.operator)
    if (filters.user) params.append('user', filters.user)
    if (filters.populate) {
      params.append('populate', filters.populate)
    }
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().strappBackend}sims${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching SIMs:', error)
        throw error
      })
  },

  /**
   * Demande l'activation d'une SIM
   * @param {Object} activationData - Données activation
   * @param {string} activationData.simId - ID de la SIM
   * @param {string} activationData.mode - Mode (urgent, onDelivery, scheduled)
   * @param {string} activationData.activationDate - Date activation si scheduled
   * @param {boolean} activationData.portability - Si portabilité
   * @param {string} activationData.RIO - Code RIO si portabilité
   * @param {string} activationData.portabilityDate - Date portabilité
   * @param {string} activationData.portabilityNumber - Numéro à porter
   * @returns {Promise<Object>} Résultat
   */
  activateSIM(activationData) {
    const normalizedActivationData = {
      ...activationData,
      activationMode: activationData?.activationMode || activationData?.mode,
      subscriptionId:
        activationData?.subscriptionId ||
        activationData?.subscription?.id ||
        (typeof activationData?.subscription === 'string' ? activationData.subscription : undefined)
    }

    if (!normalizedActivationData.simId) {
      throw new Error('Missing required field: simId')
    }

    if (!normalizedActivationData.activationMode) {
      throw new Error('Missing required field: activationMode')
    }

    if (!normalizedActivationData.subscriptionId) {
      throw new Error('Missing required field: subscriptionId')
    }

    return fetchWithToken(`${config().strappBackend}sims/activate`, {
      method: 'PUT',
      body: JSON.stringify(normalizedActivationData)
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error activating SIM:', error)
        throw error
      })
  },

  /**
   * Récupère un device Strapp par son ID
   * @param {string} deviceId - ID du device
   * @param {boolean} force - Forcer le refresh cache
   * @returns {Promise<Object>} Détails du device
   */
  getDeviceById(deviceId, force = false) {
    return this.getDevices({
      id: deviceId,
      limit: 1,
      force,
      populate: 'model,user,site,billingEntity'
    })
      .then(response => {
        const devices = response?.data || response?.results || []
        if (devices.length > 0) {
          return devices[0]
        }
        throw new Error(`Device with id ${deviceId} not found`)
      })
  },

  getDeviceHistory(deviceId, force = false) {
    const url = `${config().strappBackend}devices/${deviceId}/history${force ? '?force=true' : ''}`
    return fetchWithToken(url, { method: 'GET' })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => { throw new Error(error.error || error.message || 'API Error') })
        }
        return response.json()
      })
  },

  getSimHistory(simId, force = false) {
    const url = `${config().strappBackend}sims/${simId}/history${force ? '?force=true' : ''}`
    return fetchWithToken(url, { method: 'GET' })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => { throw new Error(error.error || error.message || 'API Error') })
        }
        return response.json()
      })
  },

  requestDeviceRma(deviceId, claim) {
    return fetchWithToken(`${config().strappBackend}devices/${deviceId}/rma`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(claim)
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(error => { throw new Error(error.error || error.message || `Erreur API ${response.status}`) })
        }
        return response.json()
      })
  },

  getDeviceBillingLines(deviceId, force = false) {
    const url = `${config().strappBackend}devices/${deviceId}/billing-lines${force ? '?force=true' : ''}`
    return fetchWithToken(url, { method: 'GET' })
      .then(response => {
        // Strapp relaie son propre 403 quand la clé API du CRM n'a pas le droit Facturation : null = pas d'accès
        if (response.status === 403) return null
        if (!response.ok) {
          return response.json().then(error => { throw new Error(error.error || error.message || `Erreur API ${response.status}`) })
        }
        return response.json()
      })
  },

  /**
   * Demande la résiliation d'une SIM
   * @param {Object} cancelData - Données résiliation
   * @param {string} cancelData.simId - ID de la SIM
   * @param {boolean} cancelData.urgent - Résiliation urgente
   * @param {string} cancelData.date - Date résiliation (YYYY-MM-DD)
   * @param {string} cancelData.message - Motif/commentaire
   * @returns {Promise<Object>} Résultat
   */
  cancelSIM(cancelData) {
    return fetchWithToken(`${config().strappBackend}sims/cancel`, {
      method: 'POST',
      body: JSON.stringify(cancelData)
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error canceling SIM:', error)
        throw error
      })
  },

  /**
   * Récupère la consommation d'une SIM
   * @param {Object} params - Paramètres
   * @param {string} params.simId - ID SIM (ou iccid ou phoneNumber)
   * @param {string} params.iccid - ICCID (alternative)
   * @param {string} params.phoneNumber - Numéro (alternative)
   * @param {string} params.from - Date début (YYYY-MM-DD)
   * @param {string} params.to - Date fin (YYYY-MM-DD)
   * @param {string} params.aggregation - Mode (daily, monthly, total)
   * @returns {Promise<Object>} Consommation détaillée
   */
  getSIMUsage(params) {
    const queryParams = new URLSearchParams()
    
    if (params.simId) queryParams.append('simId', params.simId)
    if (params.iccid) queryParams.append('iccid', params.iccid)
    if (params.phoneNumber) queryParams.append('phoneNumber', params.phoneNumber)
    if (params.from) queryParams.append('from', params.from)
    if (params.to) queryParams.append('to', params.to)
    if (params.aggregation) queryParams.append('aggregation', params.aggregation)
    if (params.force) queryParams.append('force', 'true')

    const url = `${config().strappBackend}usage?${queryParams.toString()}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching SIM usage:', error)
        throw error
      })
  },

  /**
   * Affecte un device à un utilisateur Strapp
   * @param {string} deviceId - ID Strapp du device
   * @param {Object} assignData - Données d'affectation
   * @param {string} assignData.userId - ID de l'utilisateur destinataire (obligatoire)
   * @param {string} [assignData.deliveryDate] - Date de remise (ISO). Défaut : aujourd'hui.
   * @param {string} [assignData.deliveryLocation] - Lieu de remise
   * @param {string} [assignData.clientNotes] - Notes client
   * @returns {Promise<Object>} Résultat
   */
  assignDevice(deviceId, assignData) {
    return fetchWithToken(`${config().strappBackend}devices/${deviceId}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(assignData)
    })
      .then(response => {
        if (!response.ok) {
          // Le backend renvoie { error: "..." } : sans ce champ on n'affichait
          // qu'un « Erreur API 500 » illisible côté utilisateur.
          return response.json().catch(() => null).then(body => {
            console.error('[StrappService] assignDevice API Error:', body)
            const message = (body && (body.error || body.message)) || `Erreur API ${response.status}`
            throw new Error(message)
          })
        }
        return response.json()
      })
      .catch(error => {
        console.error(`[StrappService] Error assigning device ${deviceId}:`, error)
        throw error
      })
  },

  unassignDevice(deviceId) {
    return sendJson(`devices/${deviceId}/unassign`, 'POST')
  },

  // Tablette en propriété : Strapp refuse les statuts d'étape Strapp
  updateDeviceStatus(deviceId, status) {
    return sendJson(`devices/${deviceId}`, 'PATCH', { status })
  },

  declareDeviceClaim(deviceId, claim) {
    return sendJson(`devices/${deviceId}/claims`, 'POST', claim)
  },

  // Tablettes louées : actions de stock dédiées, qui tracent la date et la mise à disposition
  markDeviceDistributed(deviceId) {
    return sendJson(`stock/${deviceId}/distributed`, 'POST')
  },

  markDeviceRecovered(deviceId) {
    return sendJson(`stock/${deviceId}/recovered`, 'POST')
  },

  returnDeviceToStrapp(deviceId) {
    return sendJson(`stock/${deviceId}/return-to-strapp`, 'POST')
  },

  /**
   * Initie une demande de rachat
   * @param {string} deviceId - ID du device
   * @returns {Promise<Object>} Résultat
   */
  createBuyback(deviceId) {
    return fetchWithToken(`${config().strappBackend}buybacks`, {
      method: 'POST',
      body: JSON.stringify({ smartphoneId: deviceId })
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error creating buyback:', error)
        throw error
      })
  },

  /**
   * Liste les commandes
   * @param {Object} filters - Filtres
   * @returns {Promise<Object>} Commandes
   */
  getOrders(filters = {}) {
    const params = new URLSearchParams()
    
    if (filters.id) params.append('id', filters.id)
    if (filters.orderNumber) params.append('orderNumber', filters.orderNumber)
    if (filters.status) params.append('status', filters.status)
    if (filters.populate && filters.populate.length > 0) {
      params.append('populate', filters.populate.join(','))
    }
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.cursor !== undefined) params.append('cursor', filters.cursor)
    if (filters.force) params.append('force', 'true')

    const queryString = params.toString()
    const url = `${config().strappBackend}orders${queryString ? '?' + queryString : ''}`

    return fetchWithToken(url, {
      method: 'GET'
    })
      .then(response => response.json())
      .catch(error => {
        console.error('[StrappService] Error fetching orders:', error)
        throw error
      })
  },

  /**
   * Passe une nouvelle commande
   * @param {Object} orderData - Données commande
   * @param {Array<Object>} orderData.items - Items (smartphones/SIM)
   * @param {string} orderData.deliveryMode - Mode livraison (asap, scheduled, urgent)
   * @param {string} orderData.deliveryDate - Date si scheduled (ISO-8601)
   * @param {string} orderData.addressType - Type adresse (site, user, existing, inline)
   * @param {string} orderData.siteId - ID site si type=site
   * @param {string} orderData.userId - ID user si type=user
   * @param {string} orderData.addressId - ID adresse si type=existing
   * @param {Object} orderData.inlineAddress - Adresse inline si type=inline
   * @param {string} orderData.contactUserId - ID contact commande
   * @param {string} orderData.deliveryContactUserId - ID contact livraison
   * @param {string} orderData.deliveryContactNumber - Tél contact livraison
   * @param {number} orderData.duration - Durée engagement (0, 12, 18, 24, 36)
   * @returns {Promise<Object>} Commande créée
   */
  createOrder(orderData) {
    return fetchWithToken(`${config().strappBackend}orders`, {
      method: 'POST',
      body: JSON.stringify(orderData)
    })
      .then(response => response.json().catch(() => null).then(data => ({ ok: response.ok, status: response.status, data })))
      .then(({ ok, status, data }) => {
        // Le proxy/Strapp renvoie un { error } sur rejet : on rejette pour ne pas afficher un faux succès
        if (!ok) {
          const msg = (data && (data.error || data.message)) || `Échec de la commande (HTTP ${status})`
          throw new Error(msg)
        }
        return data
      })
      .catch(error => {
        console.error('[StrappService] Error creating order:', error)
        throw error
      })
  }
}
